// =============================================================================================
// ==== Utworzony w Krok 4A >>>>

import React from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FormSection } from '../../../types/forms';

interface DateRangePickerFieldProps {
  section: FormSection;
  value?: { dateFrom: string; dateTo: string };
  onChange: (value: { dateFrom: string; dateTo: string } | null) => void;
  error?: string;
}

export const DateRangePickerField: React.FC<DateRangePickerFieldProps> = ({
  section,
  value,
  onChange,
  error
}) => {
  // Parse config options
  const isDatePartOn = section.config?.isDatePartOn !== false; // default true
  const isTimePartOn = section.config?.isTimePartOn === true; // default false
  
  // Convert string values to dayjs objects
  const fromValue = value?.dateFrom ? dayjs(value.dateFrom) : null;
  const toValue = value?.dateTo ? dayjs(value.dateTo) : null;
  
  // Handle from date change
  const handleFromChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      onChange({
        dateFrom: newValue.toISOString(),
        dateTo: value?.dateTo || ''
      });
    } else {
      onChange(null);
    }
  };
  
  // Handle to date change
  const handleToChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      onChange({
        dateFrom: value?.dateFrom || '',
        dateTo: newValue.toISOString()
      });
    } else {
      onChange(null);
    }
  };
  
  // Determine which picker components to render
  const renderPicker = (
    label: string,
    value: Dayjs | null,
    onChange: (value: Dayjs | null) => void
  ) => {
    if (isDatePartOn && isTimePartOn) {
      // Both date and time
      return (
        <DateTimePicker
          label={label}
          value={value}
          onChange={onChange}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!error,
              required: !!section.validation?.required
            }
          }}
        />
      );
    } else if (isDatePartOn) {
      // Date only
      return (
        <DatePicker
          label={label}
          value={value}
          onChange={onChange}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!error,
              required: !!section.validation?.required
            }
          }}
        />
      );
    } else if (isTimePartOn) {
      // Time only
      return (
        <TimePicker
          label={label}
          value={value}
          onChange={onChange}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!error,
              required: !!section.validation?.required
            }
          }}
        />
      );
    } else {
      // Fallback
      return (
        <DatePicker
          label={label}
          value={value}
          onChange={onChange}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!error,
              required: !!section.validation?.required
            }
          }}
        />
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: 2 }}>
        {/* Label */}
        <Typography variant="subtitle1" component="label" sx={{ mb: 1, display: 'block' }}>
          {section.label}
          {section.validation?.required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
        
        {/* Description */}
        {section.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {section.description}
          </Typography>
        )}
        
        {/* Date Range Pickers */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            {renderPicker('Od', fromValue, handleFromChange)}
          </Box>
          <Box sx={{ flex: 1 }}>
            {renderPicker('Do', toValue, handleToChange)}
          </Box>
        </Box>
        
        {/* Error Message */}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};